import type { SxProps, Theme } from '@mui/material';

import { colors } from './tokens';

export function getHeaderChrome(isDark: boolean) {
  return isDark ? colors.headerChrome.dark : colors.headerChrome.light;
}

export function getLapMetaChrome(isDark: boolean) {
  return isDark ? colors.lapMetaChrome.dark : colors.lapMetaChrome.light;
}

export function getFootageChrome(isDark: boolean) {
  return isDark ? colors.footageChrome.dark : colors.footageChrome.light;
}

export function getTraceChrome(isDark: boolean) {
  return isDark ? colors.traceChrome.dark : colors.traceChrome.light;
}

export function getSessionDrawerChrome(isDark: boolean) {
  return isDark ? colors.sessionDrawerChrome.dark : colors.sessionDrawerChrome.light;
}

export function headerOutlinedButtonSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    borderColor: chrome.buttonBorder,
    color: chrome.buttonText,
    bgcolor: chrome.buttonBg,
    '&:hover': {
      borderColor: chrome.buttonBorderHover,
      bgcolor: chrome.buttonBgHover,
    },
  };
}

export function headerSectionLabelSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    fontSize: '0.72rem',
    fontWeight: 750,
    color: chrome.sectionLabel,
    letterSpacing: '0.04em',
    lineHeight: 1.25,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };
}

export function headerReplayBarSimpleSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 0.8,
    width: '100%',
    minWidth: 0,
    height: 38,
    px: 0.65,
    py: 0.45,
    borderRadius: '8px',
    border: `1px solid ${chrome.replayBarBorder}`,
    bgcolor: chrome.replayBarBg,
    boxShadow: chrome.replayBarShadow,
  };
}

export function headerReplayPlayButtonSx(isDark: boolean, isPlaying: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    width: 28,
    height: 28,
    flexShrink: 0,
    border: `1px solid ${chrome.replayPlayBorder}`,
    color: isDark ? '#FFFFFF' : colors.primary[600],
    bgcolor: isPlaying ? chrome.replayPlayBgActive : chrome.replayPlayBg,
    '&:hover': { bgcolor: chrome.replayPlayBgHover },
  };
}

export function headerWeatherChipSx(isDark: boolean, variant: 'inline' | 'header' = 'inline'): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  if (variant === 'header') {
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      minHeight: 40,
      px: 0.25,
      py: 0,
      cursor: 'pointer',
      minWidth: 0,
      maxWidth: 260,
      flexShrink: 0,
      border: 'none',
      bgcolor: 'transparent',
      borderRadius: '8px',
      '&:hover': {
        bgcolor: chrome.weatherHoverBg,
      },
    };
  }

  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1,
    py: 0.45,
    borderRadius: '8px',
    border: 'none',
    bgcolor: 'transparent',
    cursor: 'pointer',
    minWidth: 128,
    maxWidth: 200,
    '&:hover': {
      bgcolor: chrome.weatherHoverBg,
    },
  };
}

export function headerWeatherPanelSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    display: { xs: 'none', lg: 'flex' },
    alignItems: 'center',
    flexShrink: 0,
    ml: 'auto',
    pl: 1.5,
    borderLeft: `1px solid ${chrome.weatherDivider}`,
  };
}

export function mapPlaybackDockOuterSx(isDark: boolean): SxProps<Theme> {
  return {
    position: 'absolute',
    left: { xs: 8, md: 12 },
    right: { xs: 8, md: 12 },
    bottom: { xs: 8, md: 12 },
    zIndex: 8,
    pointerEvents: 'none',
  };
}

export function mapPlaybackDockShellSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    pointerEvents: 'auto',
    borderRadius: '10px',
    border: `1px solid ${chrome.mapDockBorder}`,
    bgcolor: chrome.mapDockBg,
    boxShadow: chrome.mapDockShadow,
    backdropFilter: 'blur(10px)',
    overflow: 'hidden',
  };
}

export function mapPlaybackDockLegendSx(isDark: boolean): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  return {
    px: 1.1,
    py: 0.6,
    borderBottom: `1px solid ${chrome.mapDockBorder}`,
    bgcolor: chrome.mapDockLegendBg,
  };
}

export function mapPlaybackDockReplaySx(): SxProps<Theme> {
  return {
    px: 0.75,
    py: 0.75,
  };
}

export function lapMetaDrawerPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getLapMetaChrome(isDark);
  return {
    maxHeight: { xs: '72vh', md: '52vh' },
    bgcolor: chrome.paperBg,
    borderTop: `1px solid ${chrome.border}`,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    overflow: 'hidden',
  };
}

export function lapMetaDrawerHeaderSx(isDark: boolean): SxProps<Theme> {
  const chrome = getLapMetaChrome(isDark);
  return {
    px: { xs: 1.5, md: 2 },
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`,
  };
}

export function lapMetaToggleGroupSx(isDark: boolean): SxProps<Theme> {
  const chrome = getLapMetaChrome(isDark);
  return {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
      width: 76,
      px: 1,
      py: 0.35,
      fontSize: '0.68rem',
      fontWeight: 800,
      textTransform: 'none',
      color: chrome.toggleText,
      borderColor: chrome.toggleBorder,
      '&.Mui-selected': {
        color: chrome.toggleSelectedText,
        bgcolor: chrome.toggleSelectedBg,
      },
      '&.Mui-selected:hover': {
        bgcolor: chrome.toggleSelectedBg,
      },
    },
  };
}

export function lapMetaTableContainerSx(isDark: boolean): SxProps<Theme> {
  const chrome = getLapMetaChrome(isDark);
  return {
    maxHeight: { xs: '54vh', md: '38vh' },
    border: `1px solid ${chrome.tableBorder}`,
    borderRadius: 1,
    bgcolor: chrome.tableBg,
  };
}

export function footageDrawerPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    width: { xs: '100%', sm: 420 },
    maxWidth: '100vw',
    bgcolor: chrome.paperBg,
    borderLeft: `1px solid ${chrome.border}`,
    color: chrome.title,
  };
}

export function footageDrawerHeaderSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    flexShrink: 0,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`,
  };
}

export function footageCardSx(isDark: boolean, muted = false): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    border: `1px solid ${chrome.cardBorder}`,
    borderRadius: 1,
    bgcolor: muted ? chrome.cardBgMuted : chrome.cardBg,
    p: muted ? 1 : 1.25,
  };
}

export function footageTextFieldSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    '& .MuiInputLabel-root': { color: chrome.inputLabel, fontSize: '0.72rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: chrome.inputLabelFocus },
    '& .MuiInputBase-root': {
      color: chrome.inputText,
      fontFamily: 'monospace',
      fontSize: '0.72rem',
      bgcolor: chrome.inputBg,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorder },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderHover },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderFocus },
    '& .MuiFormHelperText-root': { color: chrome.helperText, mx: 0, fontSize: '0.62rem' },
  };
}

export function uploadDialogPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    bgcolor: chrome.paperBg,
    color: chrome.title,
    border: `1px solid ${chrome.border}`,
    borderRadius: 2,
    backgroundImage: 'none',
  };
}

export function uploadDialogSelectSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  return {
    color: chrome.inputText,
    fontSize: '0.78rem',
    bgcolor: chrome.inputBg,
    '.MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorder },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderHover },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderFocus },
  };
}

export function uploadChoiceButtonSx(isDark: boolean): SxProps<Theme> {
  const chrome = getFootageChrome(isDark);
  const trace = getTraceChrome(isDark);
  return {
    borderColor: chrome.cardBorder,
    bgcolor: isDark ? 'rgba(255,255,255,0.025)' : chrome.cardBg,
    color: chrome.title,
    '& .MuiButton-startIcon': {
      mt: 0.2,
      color: trace.iconAccent,
    },
    '&:hover': {
      borderColor: isDark ? 'rgba(124,58,237,0.72)' : trace.iconAccent,
      bgcolor: isDark ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.08)',
    },
  };
}

export function sessionDrawerPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    width: { xs: '100%', sm: 400 },
    maxWidth: '100vw',
    bgcolor: chrome.paperBg,
    backgroundColor: chrome.paperBg,
    backgroundImage: 'none',
    borderLeft: `1px solid ${chrome.border}`,
    borderRight: 'none',
    color: chrome.title,
  };
}

export function sessionDrawerHeaderSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    flexShrink: 0,
    px: 1.5,
    py: 1.25,
    borderBottom: `1px solid ${chrome.headerBorder}`,
  };
}

export function sessionDrawerSectionLabelSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    fontSize: '0.72rem',
    fontWeight: 800,
    color: chrome.sectionLabel,
    letterSpacing: '0.04em',
  };
}

export function sessionDrawerResetButtonSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    minWidth: 0,
    px: 1,
    py: 0.35,
    textTransform: 'none',
    fontSize: '0.68rem',
    fontWeight: 700,
    color: chrome.resetText,
    border: `1px solid ${chrome.resetBorder}`,
    borderRadius: '6px',
    '&:hover': {
      bgcolor: chrome.resetHoverBg,
      borderColor: chrome.inputBorderHover,
    },
  };
}

export function sessionDrawerIconButtonSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    color: chrome.closeText,
    border: `1px solid ${chrome.closeBorder}`,
    borderRadius: '6px',
  };
}

export function sessionDrawerToggleGroupSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    '& .MuiToggleButton-root': {
      px: 1.25,
      py: 0.35,
      fontSize: '0.68rem',
      fontWeight: 750,
      textTransform: 'none',
      color: chrome.toggleText,
      borderColor: chrome.toggleBorder,
      '&.Mui-selected': {
        color: chrome.toggleSelectedText,
        bgcolor: chrome.toggleSelectedBg,
      },
    },
  };
}

export function sessionDrawerSelectSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    color: chrome.inputText,
    bgcolor: chrome.inputBg,
    fontSize: '0.78rem',
    '.MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorder },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderHover },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: chrome.inputBorderHover },
  };
}

export function sessionDrawerCardSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    border: `1px solid ${chrome.cardBorder}`,
    borderRadius: 1.25,
    bgcolor: chrome.cardBg,
  };
}

export function sessionDrawerCollapsibleSectionSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    border: `1px solid ${chrome.sectionCardBorder}`,
    borderRadius: '8px',
    p: 1.25,
    bgcolor: chrome.sectionCardBg,
  };
}

export function sessionDrawerSelectMenuButtonSx(isDark: boolean, open: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    justifyContent: 'space-between',
    px: 1.25,
    py: 0.85,
    textTransform: 'none',
    borderColor: open ? colors.primary[500] : chrome.inputBorder,
    color: chrome.menuPrimaryText,
    bgcolor: chrome.inputBg,
    '&:hover': {
      borderColor: chrome.inputBorderHover,
      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F9FAFB',
    },
  };
}

export function sessionDrawerSelectMenuPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    bgcolor: chrome.menuBg,
    color: chrome.menuPrimaryText,
    border: `1px solid ${chrome.menuBorder}`,
    borderRadius: 1,
    boxShadow: chrome.menuShadow,
  };
}

export function sessionDrawerLapTableShellSx(isDark: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    borderRadius: 1.25,
    border: `1px solid ${chrome.tableBorder}`,
    overflow: 'hidden',
    bgcolor: chrome.tableBg,
  };
}

export function sessionDrawerCompareAddButtonSx(isDark: boolean, enabled: boolean): SxProps<Theme> {
  const chrome = getSessionDrawerChrome(isDark);
  return {
    justifyContent: 'flex-start',
    py: 1.1,
    px: 1.25,
    textTransform: 'none',
    fontSize: '0.78rem',
    fontWeight: 700,
    color: enabled ? chrome.addCompareText : chrome.addCompareDisabledText,
    borderColor: chrome.addCompareBorder,
    borderStyle: 'dashed',
    borderRadius: 1.25,
    '&:hover': {
      borderColor: colors.primary[500],
      bgcolor: `${colors.primary[500]}12`,
      borderStyle: 'dashed',
    },
  };
}

export type SessionDrawerLapKind = 'flying' | 'out' | 'in' | 'crash';

export function sessionDrawerLapToneStyles(
  isDark: boolean,
  kind: SessionDrawerLapKind,
  selected: boolean,
) {
  const chrome = getSessionDrawerChrome(isDark);
  switch (kind) {
    case 'out':
      return {
        color: selected ? chrome.lapOutSelected : chrome.lapOut,
        bg: selected ? chrome.lapOutSelectedBg : 'transparent',
      };
    case 'in':
      return {
        color: selected ? chrome.lapInSelected : chrome.lapIn,
        bg: selected ? chrome.lapInSelectedBg : 'transparent',
      };
    case 'crash':
      return {
        color: selected ? chrome.lapCrashSelected : chrome.lapCrash,
        bg: selected ? chrome.lapCrashSelectedBg : 'transparent',
      };
    default:
      return {
        color: selected ? chrome.lapFlyingSelected : chrome.lapFlying,
        bg: selected ? chrome.lapFlyingSelectedBg : 'transparent',
      };
  }
}

export function traceDrawerShellSx(isDark: boolean): SxProps<Theme> {
  const chrome = getTraceChrome(isDark);
  return {
    bgcolor: chrome.shellBg,
    color: chrome.title,
    borderTop: `1px solid ${chrome.border}`,
    overflow: 'hidden',
  };
}

export function traceDrawerHeaderSx(isDark: boolean): SxProps<Theme> {
  const chrome = getTraceChrome(isDark);
  return {
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 1.4,
    py: 0.9,
    borderBottom: `1px solid ${chrome.headerBorder}`,
    bgcolor: chrome.shellHeaderBg,
  };
}

export function tracePanelSx(isDark: boolean, drawer = false): SxProps<Theme> {
  const chrome = getTraceChrome(isDark);
  return {
    flexShrink: 0,
    borderTop: drawer ? 0 : `1px solid ${chrome.border}`,
    bgcolor: chrome.panelBg,
    px: drawer ? 1.4 : 1.2,
    py: drawer ? 1 : 0.75,
    maxHeight: drawer ? 'min(48vh, 420px)' : 240,
    minHeight: 0,
    overflow: 'hidden',
  };
}

export function tracePickerDrawerPaperSx(isDark: boolean): SxProps<Theme> {
  const chrome = getTraceChrome(isDark);
  return {
    width: { xs: '86vw', sm: 340 },
    maxWidth: '100vw',
    bgcolor: chrome.pickerBg,
    color: chrome.title,
    borderLeft: `1px solid ${chrome.pickerBorder}`,
    boxShadow: isDark ? '0 0 36px rgba(0,0,0,0.46)' : '0 8px 24px rgba(15,23,42,0.12)',
  };
}

export function mapOverlayButtonSx(isDark: boolean, active = false): SxProps<Theme> {
  const chrome = getHeaderChrome(isDark);
  const base = {
    height: 30,
    px: 1.1,
    borderRadius: active ? '8px' : '6px',
    textTransform: 'none',
    fontSize: '0.74rem',
    fontWeight: 850,
    flexShrink: 0,
  } as const;

  if (active) {
    return {
      ...base,
      bgcolor: isDark ? 'rgba(183,140,255,0.88)' : 'rgba(124,58,237,0.14)',
      color: isDark ? '#FFFFFF' : colors.primary[700],
      border: isDark ? '1px solid rgba(183,140,255,0.45)' : `1px solid ${colors.primary[200]}`,
      boxShadow: isDark ? '0 8px 22px rgba(0,0,0,0.3)' : 'none',
      '&:hover': {
        bgcolor: isDark ? 'rgba(183,140,255,0.96)' : 'rgba(124,58,237,0.2)',
      },
    };
  }

  return {
    ...base,
    bgcolor: isDark ? 'rgba(1,13,24,0.82)' : chrome.buttonBg,
    color: isDark ? '#FFFFFF' : chrome.buttonText,
    border: `1px solid ${isDark ? 'rgba(36,62,82,0.72)' : chrome.buttonBorder}`,
    boxShadow: isDark ? '0 8px 22px rgba(0,0,0,0.3)' : 'none',
    '&:hover': {
      bgcolor: isDark ? 'rgba(12,27,42,0.94)' : chrome.buttonBgHover,
      borderColor: isDark ? 'rgba(36,62,82,0.72)' : chrome.buttonBorderHover,
    },
    '&.Mui-disabled': {
      color: isDark ? 'rgba(255,255,255,0.48)' : '#9CA3AF',
      bgcolor: isDark ? 'rgba(1,13,24,0.62)' : '#F9FAFB',
    },
  };
}
